import axios, { AxiosResponse } from "axios";
import { axiosInstance } from "./common";

export enum DocumentScope {
  CASE = "case",
  PARTY = "party",
  LOAN_ACCOUNT = "loan_account",
  LEA="Lea",
  PRE_SCN_FIC_MEETING = "pre_scn_fic_meeting",
}

export enum DocumentCardinality {
  SINGLETON = "singleton",
  MULTIPLE = "multiple",
}

export enum DocumentUrlMode{
  VIEW = "VIEW",
  DOWNLOAD = "DOWNLOAD"
}


export enum DocumentType {
  INVESTIGATION_REPORT = "INVESTIGATION_REPORT",
  SCN = "SCN",
  SCN_RESPONSE = "SCN_RESPONSE",
  COMMITTEE_REPORT = "COMMITTEE_REPORT",
  REASONED_ORDER = "REASONED_ORDER",
  FMR1 = "FMR1",
  LEA_REPORT = "LEA_REPORT",
  SAE_REPORT = "SAE_REPORT",
  OTHERS = "OTHERS",
  CASE_BACKGROUND = "CASE_BACKGROUND",
  RECOVERY_SARFAESI = "RECOVERY_SARFAESI",
  RECOVERY_DRT = "RECOVERY_DRT",
  RECOVERY_NCLT = "RECOVERY_NCLT",
  RECOVERY_COURT_CASE = "RECOVERY_COURT_CASE",
  IO_APPOINTING_AUTH_REVIEW = "IO_APPOINTING_AUTH_REVIEW",
  INVESTIGATION_SUPPORTING = "INVESTIGATION_SUPPORTING",
}

export enum DocumentEntityType {
  CASE = "CASE",
  PARTY_INVOLVED = "PARTY_INVOLVED",
  LOAN_ACCOUNT = "LOAN_ACCOUNT",
  LEA = "LEA",
  PRE_SCN_FIC_MEETING = "PRE_SCN_FIC_MEETING",
  IO_APPOINTING_AUTH_ASSESSMENT = "IO_APPOINTING_AUTH_ASSESSMENT",
}

export enum DocumentStatus {
  ACTIVE = "ACTIVE",
  DELETED = "DELETED",
  REPLACED = "REPLACED",
}

export enum DocumentFileType {
  PDF = "PDF",
}

export interface Document {
  document_id: string;
  document_type: DocumentType;
  entity_type: DocumentEntityType;
  entity_id: string;
  file_name: string;
  file_type: DocumentFileType;
  file_reference: string;
  notes?: string;
  created_at: string;
  status: DocumentStatus;
}

export interface CaseDocument extends Document {
  partyName?: string | null;
  loanAccountId?: string |null;
  leaName?: string |null; 
  deleting?: boolean;
  deleteError?: string | null;
  downloading?: boolean;
  downloadError?: string | null;
  replacing?: boolean;
  replaceError?: string | null;
}

export interface GetDocumentParams {
  document_id?: string;
}

export interface DocumentsResponse {
  documents: Document[];
}

export interface AddDocumentRequest {
  document_type: DocumentType;
  entity_type: DocumentEntityType;
  entity_id: string;
  file_name: string;
  file_type: DocumentFileType;
  notes?: string;
}

export interface AddDocumentResponse {
  document_id: string;
  file_reference: string;
  created_at: string;
  status: DocumentStatus;
}

export interface DownloadUrlParams {
  file_reference: string;
  mode?: "VIEW" | "DOWNLOAD";
}

export interface DownloadUrlResponse {
  download_url: string;
  expires_in: number;
}

export interface GetDocumentUploadUrlParams {
  document_type: DocumentType;
  file_name: string;
}

export interface DocumentUploadUrlResponse {
  upload_url: string;
  upload_headers: Record<string, string>;
  file_reference: string;
  document_id: string;
}

const CaseDocumentsAPI = {
  getDocuments: async (case_id: string) => {
    const response: AxiosResponse<DocumentsResponse> =
      await axiosInstance.get(`/v1/cases/${case_id}/documents`);

    return response.data;
  },

  getDocumentUploadUrl: async (case_id: string, params: GetDocumentUploadUrlParams) => {
    const response : AxiosResponse<DocumentUploadUrlResponse> = 
      await axiosInstance.get(
        `/v1/cases/${case_id}/documents/upload-url`,
        {
          params: params
        }
      )
    return response.data;
  },

  addDocument: async (
    case_id: string,
    document: Document
  ) => {

    const response: AxiosResponse<AddDocumentResponse> =
      await axiosInstance.post(`/v1/cases/${case_id}/documents`, document);

    return response.data;
  },

  replaceDocument: async (
    case_id: string,
    document_id: string,
    document: Document
  ) => {

    const response: AxiosResponse<AddDocumentResponse> =
      await axiosInstance.post(`/v1/cases/${case_id}/documents/${document_id}/replace`, document);

    return response.data;
  },

  uploadDocument: async (upload_url: string, file: File, upload_headers: Record<string, string>) => {
    await axios.put(upload_url, file, {
      headers: upload_headers,
    });
  },

  deleteDocument: async (case_id: string, document_id: string) => {
    const response: AxiosResponse<void> =
      await axiosInstance.delete(
        `/v1/cases/${case_id}/documents/${document_id}`
      );

    return response.data;
  },

  getDownloadUrl: async (params: DownloadUrlParams) => {
    const response: AxiosResponse<DownloadUrlResponse> =
      await axiosInstance.get(`/v1/documents/download-url`, { params });

    return response.data;
  },
};

export default CaseDocumentsAPI;
